import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import type { HFDistribution, ProtocolFilter } from "../types";
import { InfoTip } from "./InfoTip";

interface HealthFactorChartProps {
  distribution: HFDistribution;
  protocolFilter: ProtocolFilter;
}

export function HealthFactorChart({ distribution, protocolFilter }: HealthFactorChartProps) {
  const chartData = distribution.buckets.map((bucket) => {
    const count =
      protocolFilter === "all"
        ? bucket.count
        : bucket.by_protocol[protocolFilter]?.count ?? 0;

    return {
      label: bucket.label,
      count,
      collateral_usd: bucket.total_collateral_usd,
      lo: bucket.range[0],
    };
  });

  const s = distribution.summary;

  return (
    <div className="card">
      <h2 className="text-base font-semibold mb-1">Health Factor Distribution</h2>
      <p className="text-xs text-text-muted mb-2">
        Positions by health factor bucket (count)
      </p>

      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -8 }}>
          <XAxis
            dataKey="label"
            tick={{ fontSize: 9, fill: "#64748b" }}
            axisLine={{ stroke: "#282a3a" }}
            tickLine={false}
            interval={0}
            angle={-20}
            dy={4}
          />
          <YAxis
            tick={{ fontSize: 9, fill: "#64748b" }}
            axisLine={false}
            tickLine={false}
            width={40}
          />
          <Tooltip cursor={false}
            contentStyle={{
              backgroundColor: "#1e2030",
              border: "1px solid #282a3a",
              borderRadius: "6px",
              fontSize: "11px",
            }}
            labelStyle={{ color: "#e2e8f0" }}
            itemStyle={{ color: "#e2e8f0" }}
            formatter={(value: number, _name: string) => [value.toLocaleString(), "Positions"]}
          />
          <Bar dataKey="count" radius={[3, 3, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={index} fill={bucketColor(entry.lo)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Summary stats below the chart */}
      <div className="mt-2 pt-3 border-t border-surface-600 grid grid-cols-3 gap-3">
        <div>
          <p className="text-[10px] text-text-muted uppercase tracking-wider">
            <InfoTip text="Collateral-weighted mean health factor across all positions. Each position's HF is capped at 20 before averaging to prevent ultra-safe outliers from distorting the number.">
              Avg HF
            </InfoTip>
          </p>
          <p className={`text-lg font-mono font-semibold ${hfColor(s.weighted_avg_hf)}`}>
            {s.weighted_avg_hf.toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-[10px] text-text-muted uppercase tracking-wider">
            <InfoTip text="Number of positions with health factor below 1.1 — within 10% of the liquidation threshold. These are most at risk from small price moves.">
              Near Liq (&lt;1.1)
            </InfoTip>
          </p>
          <p className="text-lg font-mono font-semibold text-risk-warning">
            {s.positions_below_1_1.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-[10px] text-text-muted uppercase tracking-wider">
            <InfoTip text="Total collateral value of positions with HF below 1.1. This is the collateral that would enter liquidation first in any price downturn.">
              Value at Risk
            </InfoTip>
          </p>
          <p className="text-lg font-mono font-semibold text-risk-danger">
            {formatUsd(s.value_below_1_1_usd)}
          </p>
        </div>
      </div>
    </div>
  );
}

function bucketColor(lo: number): string {
  if (lo < 1.05) return "#dc2626";
  if (lo < 1.1) return "#ef4444";
  if (lo < 1.2) return "#f97316";
  if (lo < 1.5) return "#eab308";
  return "#22c55e";
}

function hfColor(hf: number): string {
  if (hf < 1.1) return "text-risk-danger";
  if (hf < 1.3) return "text-risk-warning";
  if (hf < 2.0) return "text-risk-moderate";
  return "text-risk-safe";
}

function formatUsd(value: number): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}
